import * as React from 'react'
import styled from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'

import { t } from '@owl-nest/localize'
import * as address from '@owl-nest/address'
import * as services from '@owl-nest/services'
import * as api from '@owl-nest/api-client/next'
import { Country } from '@owl-nest/config'

import { LocationPicker } from './LocationPicker/LocationPicker'
import * as type from './LocationPicker/type'
import { LocationCard } from './LocationPicker/LocationCard'

export type DropoffPickerProps = {
  addressQuery?: api.BaseAddress & {
    latitude: number
    longitude: number
  }
  countries: Country[]
  countryFilter?: string[]
  mapboxApiKey?: string
  onAddressChange: (
    address: api.BaseAddress & {
      latitude: number
      longitude: number
    },
  ) => void
  onSelect: (location: type.Location) => void
  minified?: boolean
  projectId: string | number
  selected?: string
}

export function DropoffPicker({
  addressQuery,
  mapboxApiKey,
  minified,
  projectId,
  selected,
  countries,
  onAddressChange,
  countryFilter,
  onSelect,
}: DropoffPickerProps) {
  const [autocompleteSelected, setAutocompleteSelected] = React.useState<
    (api.BaseAddress & { latitude: number; longitude: number }) | undefined
  >(undefined)

  const { response } = services.dropoffPoints.useDropoffPoints(projectId, {
    fetchOnMount: 'force',
    payload:
      autocompleteSelected === undefined
        ? undefined
        : {
            address: autocompleteSelected.address1,
            carrier: 'mondial_relay',
            city: autocompleteSelected.city,
            country: autocompleteSelected.country,
            latitude_address: autocompleteSelected.latitude,
            longitude_address: autocompleteSelected.longitude,
            provider: 'mondial_relay',
            range: 10,
            zip: autocompleteSelected.postal_code,
          },
  })

  const [displayMap, setDisplayMap] = React.useState(selected !== undefined)
  const [minifiedDropoff, setMinifiedDropoff] = React.useState(minified)

  const selectedDropoff = (response?.data?.points ?? []).find((rep) => rep.point_id === selected)

  return (
    <>
      {minifiedDropoff && location ? (
        <LocationCardStyled
          bottomRight={
            <plume.LinkAsButton
              type="button"
              tinted
              onClick={() => {
                setMinifiedDropoff(!minifiedDropoff)
              }}
            >
              {t('Change drop-off point')}
            </plume.LinkAsButton>
          }
          location={{
            coordinates: {
              lon: selectedDropoff?.longitude ?? 0,
              lat: selectedDropoff?.latitude ?? 0,
            },
            distance: selectedDropoff?.distance ?? undefined,
            name: selectedDropoff?.point_name ?? '',
            id: selectedDropoff?.point_id ?? '',
            address: selectedDropoff?.street ?? '',
            city: selectedDropoff?.city ?? '',
            postal_code: selectedDropoff?.zip ?? '',
            timetable: selectedDropoff?.day_of_week ?? [],
          }}
        />
      ) : (
        <>
          <address.AutoCompleteControl
            countries={countries}
            label={t('Search for address')}
            language={'fr'}
            onSelect={(address) => {
              handleChange(address)
              onAddressChange(address)
              setDisplayMap(true)
            }}
            countryFilter={countryFilter}
          />

          {displayMap && (
            <LocationPicker
              center={
                addressQuery === undefined
                  ? addressQuery
                  : {
                      lon: addressQuery.longitude,
                      lat: addressQuery.latitude,
                    }
              }
              mapboxApiKey={mapboxApiKey}
              locations={(response.data?.points ?? []).map((rep) => {
                return {
                  address: rep.street,
                  coordinates: {
                    lon: rep.longitude,
                    lat: rep.latitude,
                  },
                  city: rep.city,
                  postal_code: rep.zip,
                  id: rep.point_id,
                  distance: rep.distance,
                  name: rep.point_name,
                  timetable: rep.day_of_week,
                }
              })}
              selected={selected}
              onSelect={onSelect}
            />
          )}
        </>
      )}
    </>
  )

  function handleChange(
    address: api.BaseAddress & {
      latitude: number
      longitude: number
    },
  ): void {
    const latitude = address.latitude
    const longitude = address.longitude
    if (latitude === undefined || longitude === undefined) {
      // FIXME: add error
      return
    }

    setAutocompleteSelected({ ...address, longitude, latitude })
  }
}

const LocationCardStyled = styled(LocationCard)`
  border: 1px solid ${plume.COLORS.BORDER_PRIMARY};
`
